/**
 * Users List Page
 *
 * Lists all users with filtering and management capabilities
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useRoleAssumption } from '../../../context/RoleAssumptionContext';
import {
  getAllUsers,
  getAllOrganizations,
  getUsersByOrg,
  updateUserProfile,
  updateUserStatusWithSeats,
  checkSeatAvailability,
  getSeatTypeForRole,
} from '../../../services/firestore';
import type { UserProfile, Organization, UserRole, UserStatus } from '../../../services/firestore';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import './UsersList.css';

interface EditModalState {
  isOpen: boolean;
  user: UserProfile | null;
}

interface Filters {
  status: string;
  role: string;
  org: string;
  search: string;
}

const ROLE_LABELS: Record<UserRole, string> = {
  platform_admin: 'Platform Admin',
  cra_admin: 'CRA Admin',
  cra_analyst: 'CRA Analyst',
  rating_committee: 'Rating Committee',
  bank_admin: 'Bank Admin',
  bank_user: 'Bank User',
};

export const UsersList: React.FC = () => {
  const { userProfile: currentUser, isPlatformAdmin } = useAuth();
  const { effectiveIsPlatformAdmin, effectiveOrganization } = useRoleAssumption();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>({
    status: '',
    role: '',
    org: '',
    search: '',
  });
  const [editModal, setEditModal] = useState<EditModalState>({ isOpen: false, user: null });
  const [editData, setEditData] = useState<{ role: UserRole | null; status: UserStatus; org_id: string }>({
    role: 'bank_user',
    status: 'pending',
    org_id: '',
  });
  const [processing, setProcessing] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [seatWarning, setSeatWarning] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, [effectiveIsPlatformAdmin, effectiveOrganization?.id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use effectiveIsPlatformAdmin for UI, but isPlatformAdmin for Firestore access
      // When assuming a non-admin role, we show the limited view
      const shouldShowAllUsers = isPlatformAdmin && effectiveIsPlatformAdmin;

      let usersData: UserProfile[] = [];

      if (shouldShowAllUsers) {
        // Platform admin viewing as platform admin - show all users
        usersData = await getAllUsers();
      } else if (effectiveOrganization?.id) {
        // Platform admin assuming org role, or actual org admin - show org users
        usersData = await getUsersByOrg(effectiveOrganization.id);
      }

      // Ensure current user is always visible in the list (they just can't edit themselves)
      // This handles edge cases where the user might not appear in the org query
      // Only add if we're not in "all users" mode and the current user isn't already in the list
      if (!shouldShowAllUsers && currentUser && !usersData.some(u => u.uid === currentUser.uid)) {
        // Only add current user if they belong to the effective org (or no org is selected)
        if (!effectiveOrganization || currentUser.org_id === effectiveOrganization.id) {
          usersData = [currentUser, ...usersData];
        }
      }

      const orgsData = shouldShowAllUsers ? await getAllOrganizations() : [];

      setUsers(usersData);
      setOrganizations(orgsData);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (user: UserProfile) => {
    setEditModal({ isOpen: true, user });
    setEditData({
      role: user.role,
      status: user.status,
      org_id: user.org_id || '',
    });
  };

  const closeEditModal = () => {
    setEditModal({ isOpen: false, user: null });
    setModalError(null);
    setSeatWarning(null);
  };

  // Check seat availability when changing status to active
  const handleStatusChange = async (newStatus: UserStatus) => {
    setEditData(prev => ({ ...prev, status: newStatus }));
    setSeatWarning(null);

    if (!editModal.user) return;

    const currentUser = editModal.user;
    const currentRole = editData.role || currentUser.role;
    const currentOrgId = editData.org_id || currentUser.org_id;

    // Check if reactivating a user with a seat-consuming role
    if (currentUser.status === 'suspended' && newStatus === 'active' && currentRole && currentOrgId) {
      const seatType = getSeatTypeForRole(currentRole);
      if (seatType) {
        try {
          const { available, used, total } = await checkSeatAvailability(currentOrgId, currentRole);
          if (!available) {
            setSeatWarning(`Cannot reactivate: No ${seatType} seats available (${used}/${total} used)`);
          }
        } catch (err) {
          console.error('Error checking seat availability:', err);
        }
      }
    }
  };

  const handleUpdateUser = async () => {
    if (!editModal.user) return;

    try {
      setProcessing(true);
      setModalError(null);

      const originalUser = editModal.user;
      const statusChanged = originalUser.status !== editData.status;

      // If status is changing, use the seat-aware function
      if (statusChanged) {
        const result = await updateUserStatusWithSeats(
          originalUser.uid,
          editData.status,
          currentUser?.uid || 'unknown'
        );

        if (!result.success) {
          setModalError(result.error || 'Failed to update user status');
          return;
        }

        // Also update role/org if they changed
        const roleChanged = originalUser.role !== editData.role;
        const orgChanged = originalUser.org_id !== editData.org_id;

        if (roleChanged || orgChanged) {
          await updateUserProfile(originalUser.uid, {
            role: editData.role,
            org_id: editData.org_id || null,
          });
        }
      } else {
        // No status change, just update normally
        await updateUserProfile(originalUser.uid, {
          role: editData.role,
          status: editData.status,
          org_id: editData.org_id || null,
        });
      }

      await fetchData();
      closeEditModal();
    } catch (err) {
      console.error('Error updating user:', err);
      setModalError('Failed to update user');
    } finally {
      setProcessing(false);
    }
  };

  const getOrgName = (orgId: string | null | undefined) => {
    if (!orgId) return '-';
    const org = organizations.find(o => o.id === orgId);
    return org?.name || orgId;
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    if (filters.status && user.status !== filters.status) return false;
    if (filters.role && user.role !== filters.role) return false;
    if (filters.org && user.org_id !== filters.org) return false;
    if (filters.search) {
      const search = filters.search.toLowerCase();
      const matchEmail = user.email.toLowerCase().includes(search);
      const matchName = user.display_name?.toLowerCase().includes(search);
      if (!matchEmail && !matchName) return false;
    }
    return true;
  });

  // Page title changes based on effective role
  const pageTitle = effectiveIsPlatformAdmin ? 'All Users' : 'Team Members';

  if (loading) {
    return (
      <div className="users-list-page">
        <h1>{pageTitle}</h1>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="users-list-page">
      <h1>{pageTitle}</h1>

      {error && <div className="error-message">{error}</div>}

      {/* Filters */}
      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search by email or name..."
          value={filters.search}
          onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))}
          className="search-input"
        />

        <select
          value={filters.status}
          onChange={e => setFilters(prev => ({ ...prev, status: e.target.value }))}
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="suspended">Suspended</option>
        </select>

        <select
          value={filters.role}
          onChange={e => setFilters(prev => ({ ...prev, role: e.target.value }))}
        >
          <option value="">All Roles</option>
          {Object.entries(ROLE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        {effectiveIsPlatformAdmin && (
          <select
            value={filters.org}
            onChange={e => setFilters(prev => ({ ...prev, org: e.target.value }))}
          >
            <option value="">All Organizations</option>
            {organizations.map(org => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
        )}

        <button
          className="clear-filters-btn"
          onClick={() => setFilters({ status: '', role: '', org: '', search: '' })}
        >
          Clear Filters
        </button>
      </div>

      <div className="results-count">
        Showing {filteredUsers.length} of {users.length} users
      </div>

      {/* Users Table */}
      <div className="users-table">
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              {effectiveIsPlatformAdmin && <th>Organization</th>}
              <th>Status</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.uid}>
                <td className="email-cell">{user.email}</td>
                <td>{user.display_name || '-'}</td>
                <td>
                  <span className={`role-badge ${user.role || 'pending'}`}>
                    {user.role ? ROLE_LABELS[user.role] : 'No Role'}
                  </span>
                </td>
                {effectiveIsPlatformAdmin && <td>{getOrgName(user.org_id)}</td>}
                <td>
                  <span className={`status-badge ${user.status}`}>
                    {user.status}
                  </span>
                </td>
                <td>
                  {user.last_login?.toDate
                    ? user.last_login.toDate().toLocaleDateString()
                    : 'Never'}
                </td>
                <td>
                  <button
                    className="edit-btn"
                    onClick={() => openEditModal(user)}
                    disabled={user.uid === currentUser?.uid}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredUsers.length === 0 && (
          <div className="no-results">
            No users found matching your filters.
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editModal.isOpen && editModal.user && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Edit User</h2>
            <p className="modal-email">{editModal.user.email}</p>

            {/* Organization dropdown - only for platform admins */}
            {effectiveIsPlatformAdmin && (
              <div className="form-group">
                <label htmlFor="edit-org">Organization</label>
                <select
                  id="edit-org"
                  value={editData.org_id}
                  onChange={e => setEditData(prev => ({ ...prev, org_id: e.target.value }))}
                >
                  <option value="">No Organization</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>
                      {org.name} ({org.type.toUpperCase()})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="edit-role">Role</label>
              <select
                id="edit-role"
                value={editData.role || ''}
                onChange={e => setEditData(prev => ({ ...prev, role: e.target.value as UserRole }))}
              >
                {Object.entries(ROLE_LABELS)
                  .filter(([value]) => {
                    // CRA Admins can only assign CRA roles
                    if (!effectiveIsPlatformAdmin) {
                      return ['cra_admin', 'cra_analyst', 'rating_committee'].includes(value);
                    }
                    return true;
                  })
                  .map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="edit-status">Status</label>
              <select
                id="edit-status"
                value={editData.status}
                onChange={e => handleStatusChange(e.target.value as UserStatus)}
              >
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>

            {seatWarning && (
              <div className="seat-warning">
                {seatWarning}
              </div>
            )}

            {modalError && (
              <div className="modal-error">
                {modalError}
              </div>
            )}

            <div className="modal-actions">
              <button className="cancel-btn" onClick={closeEditModal} disabled={processing}>
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={handleUpdateUser}
                disabled={processing || !!seatWarning}
              >
                {processing ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersList;
